import { useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle, Info } from 'lucide-react';

interface MetadataTabProps {
  metadata: Record<string, unknown>;
}

function MetadataSection({ title, data }: { title: string; data: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-sm font-medium text-foreground w-full py-1"
      >
        {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {title}
      </button>
      {expanded && (
        <div className="ml-5 space-y-0.5">
          {Object.entries(data).map(([key, value]) => (
            <div key={key} className="flex items-start gap-2 text-xs py-0.5">
              <span className="font-medium text-muted-foreground min-w-[120px]">{key}:</span>
              <span className="text-foreground">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface Anomaly {
  field: string;
  issue: string;
  severity: string;
}

export default function MetadataTab({ metadata }: MetadataTabProps) {
  const [showRaw, setShowRaw] = useState(false);
  const anomalies: Anomaly[] = Array.isArray(metadata.anomalies) ? (metadata.anomalies as Anomaly[]) : [];

  return (
    <div className="space-y-4">
      {/* Anomalies */}
      {anomalies.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            Metadata Anomalies
          </h4>
          {anomalies.map((anomaly, i) => (
            <div key={i} className="flex items-start gap-2 p-2 rounded bg-orange-50 border border-orange-200">
              <Info className="h-3.5 w-3.5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-orange-800">{anomaly.field}</p>
                <p className="text-xs text-orange-700">{anomaly.issue}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Parsed metadata sections */}
      {metadata.exif && typeof metadata.exif === 'object' ? (
        <MetadataSection title="EXIF" data={metadata.exif as Record<string, unknown>} />
      ) : null}
      {metadata.xmp && typeof metadata.xmp === 'object' ? (
        <MetadataSection title="XMP" data={metadata.xmp as Record<string, unknown>} />
      ) : null}

      {/* Raw JSON */}
      <div>
        <button onClick={() => setShowRaw(!showRaw)} className="text-xs text-muted-foreground hover:text-foreground">
          {showRaw ? 'Hide' : 'Show'} raw metadata
        </button>
        {showRaw && (
          <pre className="mt-2 p-3 rounded bg-muted text-xs overflow-auto max-h-64">{JSON.stringify(metadata, null, 2)}</pre>
        )}
      </div>
    </div>
  );
}
